import React, { useEffect, useRef } from "react";

const Pagination = ({
  currentPage,
  totalItems,
  itemsPerPage,
  onPageChange,
  scrollToTopOnChange = true,
}) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage)); // ensure at least 1 page
  const previousTotalItemsRef = useRef(totalItems);

  const scrollToPageTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  const handlePageChange = (nextPage) => {
    if (nextPage === currentPage) return;

    onPageChange(nextPage);
    if (scrollToTopOnChange) {
      scrollToPageTop();
    }
  };

  useEffect(() => {
    const previousTotalItems = previousTotalItemsRef.current;

    if (currentPage > totalPages) {
      onPageChange(totalPages);
    } else if (currentPage > 1 && totalItems !== previousTotalItems) {
      onPageChange(1);
    }

    previousTotalItemsRef.current = totalItems;
  }, [currentPage, onPageChange, totalItems, totalPages]);

  const getPageNumbers = () => {
    const maxButtons = 5;
    const pages = [];

    let start = Math.max(currentPage - Math.floor(maxButtons / 2), 1);
    let end = start + maxButtons - 1;

    if (end > totalPages) {
      end = totalPages;
      start = Math.max(end - maxButtons + 1, 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  };

  return (
    <div className="d-flex justify-content-between align-items-center my-1">
      <div>
        Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)} to{" "}
        {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}{" "}
        entries
      </div>
      <nav>
        <ul className="pagination mb-0">
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button
              className="page-link"
              onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
            >
              &laquo;
            </button>
          </li>
          {getPageNumbers().map((page) => (
            <li
              key={page}
              className={`page-item ${page === currentPage ? "active" : ""}`}
            >
              <button
                className="page-link"
                onClick={() => handlePageChange(page)}
              >
                {page}
              </button>
            </li>
          ))}
          {totalPages > 5 && currentPage < totalPages - 2 && (
            <li className="page-item disabled">
              <span className="page-link">...</span>
            </li>
          )}
          {totalPages > 5 && currentPage < totalPages && (
            <li
              className={`page-item ${
                currentPage === totalPages ? "active" : ""
              }`}
            >
              <button
                className="page-link"
                onClick={() => handlePageChange(totalPages)}
              >
                {totalPages}
              </button>
            </li>
          )}
          <li
            className={`page-item ${
              currentPage === totalPages ? "disabled" : ""
            }`}
          >
            <button
              className="page-link"
              onClick={() =>
                handlePageChange(Math.min(currentPage + 1, totalPages))
              }
            >
              &raquo;
            </button>
          </li>
        </ul>
      </nav>
    </div>
  );
};

export default Pagination;
